import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Users, Radar, Database, Plus, FlaskConical, Target, LifeBuoy, RotateCcw, FileDown, FileText, History } from "lucide-react";
import BackupRecovery, { NudgeSettings } from "@/components/BackupRecovery";
import { EmailSettingsCard } from "@/components/EmailCompose";
import { useAuth } from "@/context/AuthContext";
import { api, API, apiError } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, Input, Select, Field, PageHeader, Panel } from "@/components/common";
import Modal from "@/components/Modal";

const ROLES = ["owner", "editor", "viewer"];

export default function Settings() {
  const { workspaceId, currentWorkspace, isOwner, canEdit, user, loadWorkspaces, selectWorkspace } = useAuth();
  const [members, setMembers] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [hasSample, setHasSample] = useState(false);
  const [siList, setSiList] = useState([]);
  const [siName, setSiName] = useState("");
  const [recoveries, setRecoveries] = useState([]);
  const [recBusy, setRecBusy] = useState("");

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const [m, d, si] = await Promise.all([
      api.get(`/workspaces/${workspaceId}/members`),
      api.get(`/discovery/status`),
      api.get(`/strategic-interests?workspace_id=${workspaceId}`),
    ]);
    setMembers(m.data); setDiscovery(d.data); setSiList(si.data);
    const sample = await api.get(`/entities?workspace_id=${workspaceId}&search=&limit=500`);
    setHasSample(sample.data.items.some((e) => e.is_sample));
    if (isOwner) {
      api.get(`/recovery-records?workspace_id=${workspaceId}&status=pending`).then((r) => setRecoveries(r.data)).catch(() => setRecoveries([]));
    }
  }, [workspaceId, isOwner]);

  useEffect(() => { setMembers(null); load(); }, [load]);

  const updateRole = async (uid, role) => {
    try { await api.put(`/workspaces/${workspaceId}/members/${uid}`, { role }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the workspace?")) return;
    try { await api.delete(`/workspaces/${workspaceId}/members/${uid}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const seedSample = async () => {
    setSampleBusy(true);
    try { await api.post(`/workspaces/${workspaceId}/seed-sample`); toast.success("Sample workspace seeded"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSampleBusy(false); }
  };
  const removeSample = async () => {
    if (!window.confirm("Remove all sample data? Your own records are not affected.")) return;
    setSampleBusy(true);
    try { const { data } = await api.delete(`/workspaces/${workspaceId}/sample`); toast.success(`Removed ${data.entities_removed} sample entities`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSampleBusy(false); }
  };
  const addInterest = async () => {
    if (!siName.trim()) return;
    try { await api.post(`/strategic-interests?workspace_id=${workspaceId}`, { name: siName }); setSiName(""); toast.success("Strategic interest added"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delInterest = async (id) => {
    if (!window.confirm("Delete this strategic interest? It will be removed from all contacts.")) return;
    try { await api.delete(`/strategic-interests/${id}?workspace_id=${workspaceId}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const deleteWorkspace = async () => {
    const name = currentWorkspace?.name || "";
    const typed = window.prompt(`This permanently deletes the workspace "${name}" and all its data. This cannot be undone.\n\nType the exact workspace name to confirm:`);
    if (typed === null) return;
    try {
      const { data } = await api.delete(`/workspaces/${workspaceId}?confirm=${encodeURIComponent(typed)}`);
      toast.success(`Workspace deleted (${data.records_removed} records removed)`);
      const { data: list } = await api.get(`/workspaces`);
      const next = (list || []).find((w) => w.id !== workspaceId);
      if (next) selectWorkspace(next.id);
      await loadWorkspaces();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const clearHistory = async () => {
    if (!window.confirm("Clear this workspace's activity history (Recent Activity)? Your contacts, groups and records are not affected. This cannot be undone.")) return;
    try {
      const { data } = await api.delete(`/workspaces/${workspaceId}/audit-log`);
      toast.success(`Activity history cleared (${data.removed} entr${data.removed === 1 ? "y" : "ies"})`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const restoreRecovery = async (id) => {
    setRecBusy(id);
    try {
      const { data } = await api.post(`/recovery-records/${id}/restore?workspace_id=${workspaceId}`);
      const n = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Recovered ${n} record(s)`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setRecBusy(""); }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <PageHeader title="Settings" sub="Users, permissions, discovery connections, and workspace preferences."
        actions={<Btn variant="secondary" data-testid="new-workspace-btn" onClick={() => setWsOpen(true)}><Plus size={15} /> New workspace</Btn>} />

      <div className="mb-8">
        <div className="si-label mb-2">Current workspace</div>
        <Panel className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-lg" style={{ color: "var(--si-text)" }}>{currentWorkspace?.name}</div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--si-text-2)" }}>id {workspaceId} · your role: {currentWorkspace?.role}</div>
            </div>
            {isOwner && (
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <Btn variant="ghost" data-testid="clear-history-btn" onClick={clearHistory} title="Clear this workspace's Recent Activity history (records are not affected)">
                  <History size={15} /> Clear activity history
                </Btn>
                <Btn variant="ghost" style={{ color: "var(--si-danger)" }} data-testid="delete-workspace-btn" onClick={deleteWorkspace} title="Permanently delete this workspace">
                  <Trash2 size={15} /> Delete workspace
                </Btn>
              </div>
            )}
          </div>
        </Panel>
      </div>

      {/* Members */}
      <EmailSettingsCard workspaceId={workspaceId} canEdit={canEdit} />

      <div className="mb-8">
        <div className="si-label flex items-center gap-1.5 mb-2"><FileText size={13} /> Help & documentation</div>
        <div className="p-5 flex flex-wrap gap-2" style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 2 }} data-testid="docs-panel">
          {[["quick-start", "Quick Start"], ["operations-manual", "Operations Manual"], ["email-setup", "Email Setup"], ["gmail-setup", "Gmail Setup"]].map(([slug, label]) => (
            <div key={slug} className="flex items-center gap-1" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
              <a href={`${API}/docs/${slug}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 text-sm" style={{ color: "var(--si-teal-light)" }} data-testid={`doc-view-${slug}`}>{label}</a>
              <a href={`${API}/docs/${slug}?download=true`} className="px-2 py-1.5 si-label" title="Download" data-testid={`doc-dl-${slug}`}>↓</a>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="si-label flex items-center gap-1.5"><Users size={13} /> Members & permissions</div>
          {isOwner && <Btn variant="secondary" className="!py-1 !px-3 text-xs" onClick={() => setAddOpen(true)} data-testid="add-member-btn"><UserPlus size={13} /> Add member</Btn>}
        </div>
        {!members ? <Loading /> : (
          <Panel>
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <div>
                  <div className="text-sm" style={{ color: "var(--si-text)" }}>{m.name || m.email} {m.user_id === user?.id && <span className="si-label ml-1">you</span>}</div>
                  <div className="si-label mt-0.5">{m.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {isOwner && m.user_id !== currentWorkspace?.owner_id ? (
                    <Select value={m.role} onChange={(e) => updateRole(m.user_id, e.target.value)} style={{ width: "auto" }} data-testid={`role-${m.user_id}`}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</Select>
                  ) : <Badge value={m.role === "owner" ? "confirmed" : "candidate"}>{m.role}</Badge>}
                  {isOwner && m.user_id !== currentWorkspace?.owner_id && <button onClick={() => removeMember(m.user_id)} style={{ color: "var(--si-danger)" }} data-testid={`remove-${m.user_id}`}><Trash2 size={15} /></button>}
                </div>
              </div>
            ))}
          </Panel>
        )}
        <div className="text-xs mt-2" style={{ color: "var(--si-text-2)" }}>Owner: full control. Editor: create and edit records. Viewer: read-only. Access is enforced server-side; cross-workspace access is blocked.</div>
      </div>

      {/* Discovery */}
      <div className="mb-8">
        <div className="si-label flex items-center gap-1.5 mb-2"><Radar size={13} /> Discovery connections</div>
        {!discovery ? <Loading /> : (
          <Panel>
            {discovery.connections.map((c) => (
              <div key={c.id} className="px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: "var(--si-text)" }}>{c.name}</span>
                  <Badge value={c.connected ? "confirmed" : "disputed"}>{c.connected ? "Connected" : "Not connected"}</Badge>
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--si-text-2)" }}>{c.instructions}</div>
              </div>
            ))}
          </Panel>
        )}
      </div>

      {/* Strategic interests */}
      <div className="mb-8">
        <div className="si-label flex items-center gap-1.5 mb-2"><Target size={13} /> Strategic interests</div>
        <Panel>
          {canEdit && (
            <div className="flex gap-2 px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
              <Input value={siName} onChange={(e) => setSiName(e.target.value)} placeholder="e.g. Local Market Expansion" onKeyDown={(e) => e.key === "Enter" && addInterest()} data-testid="si-name" />
              <Btn variant="secondary" onClick={addInterest} data-testid="si-add"><Plus size={14} /> Add</Btn>
            </div>
          )}
          {siList.length === 0 ? <div className="text-sm px-5 py-6 text-center" style={{ color: "var(--si-text-2)" }}>No strategic interests yet. Create themes to associate contacts with, then filter the Contact Web by them.</div> : (
            siList.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <div className="flex items-center gap-2">
                  <span style={{ width: 10, height: 10, background: s.color || "var(--si-teal)", borderRadius: 2, display: "inline-block" }} />
                  <span className="text-sm" style={{ color: "var(--si-text)" }}>{s.name}</span>
                  <span className="si-label">{s.entity_count} contacts</span>
                </div>
                <div className="flex items-center gap-2">
                  <button title="Strategic Interest PDF report" data-testid={`si-report-${s.id}`} onClick={() => window.open(`${api.defaults.baseURL}/reports/strategic-interest/${s.id}.pdf?workspace_id=${workspaceId}`, "_blank")} style={{ color: "var(--si-text-2)" }}><FileDown size={15} /></button>
                  {canEdit && <button onClick={() => delInterest(s.id)} style={{ color: "var(--si-danger)" }} data-testid={`si-del-${s.id}`}><Trash2 size={15} /></button>}
                </div>
              </div>
            ))
          )}
        </Panel>
      </div>

      <NudgeSettings />
      {isOwner && <BackupRecovery workspaceId={workspaceId} />}

      {/* Data recovery (owner-only) — durable records from incomplete delete rollbacks */}      {isOwner && recoveries.length > 0 && (
        <div className="mb-8" data-testid="recovery-section">
          <div className="si-label flex items-center gap-1.5 mb-2" style={{ color: "var(--si-warning)" }}><LifeBuoy size={13} /> Data recovery</div>
          <Panel>
            <div className="px-5 py-3 text-xs" style={{ borderBottom: "1px solid var(--si-border)", color: "var(--si-text-2)" }}>
              A permanent deletion could not fully roll back and its affected records were preserved here. Restore to bring the contact and its relationships, memberships, evidence and history back.
            </div>
            {recoveries.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }} data-testid={`recovery-${r.id}`}>
                <div>
                  <div className="text-sm" style={{ color: "var(--si-text)" }}>{r.entity_name || "(unnamed contact)"}</div>
                  <div className="si-label mt-0.5 font-mono">ref {r.id.slice(0, 8)} · {new Date(r.created_at).toLocaleString()} · {r.counts?.relationships || 0} rel · {r.counts?.memberships || 0} mem · {r.counts?.evidence || 0} ev</div>
                </div>
                <Btn variant="secondary" onClick={() => restoreRecovery(r.id)} disabled={recBusy === r.id} data-testid={`recovery-restore-${r.id}`}><RotateCcw size={14} /> {recBusy === r.id ? "Restoring…" : "Restore"}</Btn>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {/* Sample data */}
      {canEdit && (
        <div className="mb-8">
          <div className="si-label flex items-center gap-1.5 mb-2"><FlaskConical size={13} /> Sample workspace data</div>
          <Panel className="p-5">
            <p className="text-sm mb-4" style={{ color: "var(--si-text-2)" }}>
              A clearly-labeled fictional sample (village office origin, professional community, existing contact, 20+ entities, shared memberships, confirmed & unverified relationships, evidence, possible duplicates, due tasks). Sample records are flagged and never represent real researched people. Removing them does not affect your own records.
            </p>
            {hasSample
              ? <Btn variant="danger" onClick={removeSample} disabled={sampleBusy} data-testid="remove-sample"><Trash2 size={14} /> {sampleBusy ? "Working…" : "Remove sample data"}</Btn>
              : <Btn variant="secondary" onClick={seedSample} disabled={sampleBusy} data-testid="seed-sample"><Database size={14} /> {sampleBusy ? "Working…" : "Load sample data"}</Btn>}
          </Panel>
        </div>
      )}

      {addOpen && <AddMemberModal workspaceId={workspaceId} onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}
      {wsOpen && <NewWorkspaceModal onClose={() => setWsOpen(false)} onCreated={async (id) => { setWsOpen(false); await loadWorkspaces(); selectWorkspace(id); }} />}
    </div>
  );
}

function AddMemberModal({ workspaceId, onClose, onAdded }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const save = async () => {
    try { await api.post(`/workspaces/${workspaceId}/members`, { email, role }); toast.success("Member added"); onAdded(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title="Add member" testid="add-member-modal">
      <Field label="Email" hint="Must be a registered user."><Input data-testid="member-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" /></Field>
      <Field label="Role"><Select value={role} onChange={(e) => setRole(e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</Select></Field>
      <Btn variant="primary" className="w-full" onClick={save} data-testid="member-save">Add member</Btn>
    </Modal>
  );
}

function NewWorkspaceModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    try { const { data } = await api.post(`/workspaces`, { name }); toast.success("Workspace created"); onCreated(data.id); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title="New workspace" testid="new-workspace-modal">
      <Field label="Workspace name"><Input data-testid="ws-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Downtown Expansion" autoFocus /></Field>
      <Btn variant="primary" className="w-full" onClick={save} data-testid="ws-save">Create workspace</Btn>
    </Modal>
  );
}
